'use client';

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import type { User } from '@supabase/supabase-js';

/**
 * Lightweight auth hook for the research library.
 * Detects if the user has an active Supabase session
 * (shared with the main Aviado app on the same domain).
 *
 * Does NOT provide sign-in/sign-up — redirects to aviado.com/auth for that.
 *
 * @author Claude + Kip McClanahan (2026-03-21)
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes (e.g., user signs in on another tab)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    signInUrl: 'https://www.aviado.com/auth',
  };
}
